import { useCallback, useState } from "react";
import { featureFlags } from "../../../../config/featureFlags";

export type BudgetViewMode = "supplier" | "group";

const STORAGE_KEY = "@mmeventos:budget-view-mode";

/**
 * Visão ativa da lista de fornecedores: plana ("Por Fornecedor", como sempre
 * foi) ou agrupada ("Por Agrupamento").
 *
 * É preferência DO USUÁRIO, não do orçamento — por isso mora no
 * `localStorage` e não no documento. Gravar no orçamento faria o modo de
 * visualização de uma pessoa virar campo versionado, comparado no comparativo
 * e registrado na Auditoria.
 *
 * O padrão é "supplier": orçamento existente abre exatamente como abria antes
 * desta funcionalidade.
 *
 * Com `featureFlags.budgetGroups` desligada o modo fica PRESO em "supplier" e
 * a preferência gravada é ignorada — quem já tinha escolhido a visão agrupada
 * não fica trancado nela sem o alternador para voltar. O valor no
 * `localStorage` é preservado, então religar a flag devolve a escolha.
 */
export function useBudgetViewMode(): [
  BudgetViewMode,
  (mode: BudgetViewMode) => void
] {
  const [mode, setMode] = useState<BudgetViewMode>(() => {
    if (!featureFlags.budgetGroups) return "supplier";
    try {
      return localStorage.getItem(STORAGE_KEY) === "group"
        ? "group"
        : "supplier";
    } catch {
      // Navegador com storage bloqueado: cai no padrão, sem quebrar a tela.
      return "supplier";
    }
  });

  const update = useCallback((next: BudgetViewMode) => {
    if (!featureFlags.budgetGroups) return;
    setMode(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Preferência não persistida é aceitável; a sessão atual segue funcionando.
    }
  }, []);

  return [featureFlags.budgetGroups ? mode : "supplier", update];
}
